import { useState } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  MoneyDisplay,
  formatMoney,
  BusinessUnitSelector,
  FinancePageLayout,
  type BusinessUnit,
} from '@/components/finance';
import { useBankAccounts, useCreateBankAccount, useUpdateBankAccount, type BankAccount } from '@/hooks/finance';
import { useCreateBankAccountAdjustment, useBankAccountAdjustments } from '@/hooks/finance/useBankAccountAdjustments';
import { Building2, Plus, Pencil, ArrowUpRight, Settings2, History } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import Papa from 'papaparse';

type ImportSource = 'itau' | 'chase';

type ParsedTxn = {
  bank_txn_id?: string | null;
  booked_at: string;
  amount: number;
  currency: 'BRL' | 'USD';
  direction: 'in' | 'out';
  description?: string | null;
  reference?: string | null;
  dedupe_key: string;
  raw_payload?: Record<string, unknown>;
};

type ParseResult = {
  transactions: ParsedTxn[];
  totalRows: number;
  skippedInvalid: number;
  skippedBalance: number;
  warnings: string[];
};

type ImportBatchSummary = {
  id: string;
  source: ImportSource;
  file_name: string;
  file_format: string;
  parse_status: 'pending' | 'parsed' | 'failed';
  parse_error: string | null;
  imported_at: string;
  total_rows_received: number;
  total_rows_parsed: number;
  inserted_normalized: number;
  skipped_duplicates: number;
  skipped_invalid: number;
  warnings: string[] | null;
};

function normalizeDate(value: string, source?: ImportSource): string {
  const v = value.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return `${v.slice(0, 10)}T00:00:00.000Z`;
  if (/^\d{4}\d{2}\d{2}/.test(v)) return `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}T00:00:00.000Z`;
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(v)) {
    const [a, b, yyyy] = v.split('/');
    const first = Number(a);
    const second = Number(b);
    const sourceIsUS = source === 'chase';
    const sourceIsBR = source === 'itau';

    const isClearlyUS = first <= 12 && second > 12;
    const isClearlyBR = first > 12 && second <= 12;

    const mm = sourceIsUS || isClearlyUS ? a.padStart(2, '0') : b.padStart(2, '0');
    const dd = sourceIsUS || isClearlyUS ? b.padStart(2, '0') : a.padStart(2, '0');

    if (sourceIsBR || isClearlyBR) {
      return `${yyyy}-${b.padStart(2, '0')}-${a.padStart(2, '0')}T00:00:00.000Z`;
    }
    return `${yyyy}-${mm}-${dd}T00:00:00.000Z`;
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(v)) {
    const [dd, mm, yyyy] = v.split('-');
    return `${yyyy}-${mm}-${dd}T00:00:00.000Z`;
  }
  const parsed = new Date(v);
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
}

function isBalanceSnapshotText(value: string): boolean {
  const normalized = value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return (
    normalized.includes('saldo total disponivel') ||
    normalized.includes('saldo disponivel') ||
    normalized.includes('saldo do dia') ||
    normalized.includes('saldo final') ||
    normalized.includes('beginning balance') ||
    normalized.includes('ending balance') ||
    normalized.includes('current balance') ||
    normalized.includes('ledger balance') ||
    normalized.includes('available balance')
  );
}

function inferImportSourceFromAccount(account: BankAccount): ImportSource {
  const bank = `${account.bank_name || ''} ${account.name || ''}`.toLowerCase();
  if (bank.includes('itaú') || bank.includes('itau')) return 'itau';
  return 'chase';
}

function parseOfx(content: string, source: ImportSource): ParseResult {
  const txBlocks = content.match(/<STMTTRN>[\s\S]*?<\/STMTTRN>/gi) || [];
  const fallbackSegments = txBlocks.length
    ? []
    : content
        .split(/<STMTTRN>/i)
        .slice(1)
        .map((segment) => segment.split(/<\/STMTTRN>/i)[0] || segment);
  const blocks = txBlocks.length ? txBlocks : fallbackSegments;
  const currency: 'BRL' | 'USD' = source === 'itau' ? 'BRL' : 'USD';
  let skippedInvalid = 0;
  let skippedBalance = 0;
  const transactions = blocks.map((block, idx) => {
    const fitid = block.match(/<FITID>([^\n<]+)/i)?.[1]?.trim() || null;
    const trnType = block.match(/<TRNTYPE>([^\n<]+)/i)?.[1]?.trim().toLowerCase() || '';
    const dtpostedRaw = block.match(/<DTPOSTED>([^\n<]+)/i)?.[1]?.trim() || '';
    const trnamtRaw = block.match(/<TRNAMT>([^\n<]+)/i)?.[1]?.trim() || '0';
    const memo = block.match(/<MEMO>([^\n<]+)/i)?.[1]?.trim() || '';
    const name = block.match(/<NAME>([^\n<]+)/i)?.[1]?.trim() || '';
    const textForBalanceCheck = `${memo} ${name} ${trnType}`.toLowerCase();
    const isBalanceSnapshot =
      trnType === 'bal' ||
      trnType === 'balance' ||
      isBalanceSnapshotText(textForBalanceCheck);
    if (isBalanceSnapshot) {
      skippedBalance += 1;
      return null;
    }

    const amount = Number(trnamtRaw.replace(',', '.'));
    const bookedAt = normalizeDate(dtpostedRaw);
    if (!Number.isFinite(amount) || amount === 0 || !bookedAt) {
      skippedInvalid += 1;
      return null;
    }
    const reference = fitid || `${bookedAt}-${amount}-${idx}`;
    return {
      bank_txn_id: fitid,
      booked_at: bookedAt,
      amount: Math.abs(amount),
      currency,
      direction: (amount >= 0 ? 'in' : 'out') as 'in' | 'out',
      description: memo || name || null,
      reference,
      dedupe_key: `${source}:${reference}:${Math.abs(amount).toFixed(2)}`,
      raw_payload: { fitid, dtpostedRaw, trnamtRaw, memo, name },
    };
  }).filter(Boolean) as ParsedTxn[];

  return {
    transactions,
    totalRows: blocks.length,
    skippedInvalid,
    skippedBalance,
    warnings: [],
  };
}

function extractOfxLedgerBalance(content: string): number | null {
  const ledgerBlock = content.match(/<LEDGERBAL>[\s\S]*?<\/LEDGERBAL>/i)?.[0] || '';
  const balAmtRaw = ledgerBlock.match(/<BALAMT>([^\n<]+)/i)?.[1]?.trim() || '';
  if (!balAmtRaw) return null;
  const amount = parseAmount(balAmtRaw);
  if (!Number.isFinite(amount)) return null;
  return amount;
}

function parseAmount(value: string): number {
  if (!value) return 0;
  let normalized = value
    .trim()
    .replace(/\u00A0/g, '')
    .replace(/[A-Za-z$€£R]/g, '')
    .replace(/\s+/g, '');

  const negativeByParens = normalized.startsWith('(') && normalized.endsWith(')');
  const negativeByMinus = normalized.startsWith('-') || normalized.endsWith('-');
  const isNegative = negativeByParens || negativeByMinus;

  normalized = normalized.replace(/[()\-+]/g, '');

  const lastComma = normalized.lastIndexOf(',');
  const lastDot = normalized.lastIndexOf('.');

  if (lastComma >= 0 && lastDot >= 0) {
    if (lastDot > lastComma) {
      // 1,234.56
      normalized = normalized.replace(/,/g, '');
    } else {
      // 1.234,56
      normalized = normalized.replace(/\./g, '').replace(/,/g, '.');
    }
  } else if (lastComma >= 0) {
    const parts = normalized.split(',');
    const decimalLike = parts[parts.length - 1]?.length === 2;
    normalized = decimalLike ? normalized.replace(',', '.') : normalized.replace(/,/g, '');
  } else if (lastDot >= 0) {
    const parts = normalized.split('.');
    const decimalLike = parts[parts.length - 1]?.length === 2;
    if (!decimalLike) normalized = normalized.replace(/\./g, '');
  }

  normalized = normalized.replace(/[^0-9.]/g, '');
  const valueNumber = Number(normalized);
  if (!Number.isFinite(valueNumber)) return 0;
  return isNegative ? -valueNumber : valueNumber;
}

function normalizeHeader(value: string): string {
  return value.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function getColumnValue(row: Record<string, string>, keys: string[]): string {
  const normalizedRow = new Map<string, string>();
  for (const key of Object.keys(row)) {
    normalizedRow.set(normalizeHeader(key), String(row[key] ?? '').trim());
  }
  // Preserve caller priority: first key in `keys` wins.
  for (const key of keys) {
    const value = normalizedRow.get(normalizeHeader(key));
    if (value != null && value !== '') return value;
  }
  return '';
}

function parseCsv(content: string, source: ImportSource): ParseResult {
  const candidateDelimiters = ['', '\t', ',', ';', '|'] as const;
  const attempts = candidateDelimiters.map((delimiter) => {
    const parsed = Papa.parse<Record<string, string>>(content, {
      header: true,
      skipEmptyLines: true,
      delimiter,
      delimitersToGuess: [',', '\t', ';', '|'],
      transformHeader: (h) => h.trim(),
    });
    const rows = (parsed.data || []).filter((row) => Object.values(row).some((v) => String(v || '').trim() !== ''));
    const headerCount = Object.keys(rows[0] || {}).length;
    const score = headerCount * 1000 + rows.length;
    return { delimiter, rows, score, headerCount };
  });

  const best = attempts
    .filter((a) => a.headerCount > 1 && a.rows.length > 0)
    .sort((a, b) => b.score - a.score)[0];

  let rows = best?.rows || [];
  if (!rows.length) {
    return { transactions: [], totalRows: 0, skippedInvalid: 0, skippedBalance: 0, warnings: ['Nenhuma linha de dados identificada no CSV.'] };
  }

  // Some Chase CSV exports include preamble lines before the real header.
  const hasDateLikeHeader = Object.keys(rows[0] || {}).some((key) => {
    const h = normalizeHeader(key);
    return h === 'date' || h === 'posting date' || h === 'transaction date' || h === 'booking date';
  });

  if (!hasDateLikeHeader) {
    const matrix = Papa.parse<string[]>(content, {
      header: false,
      skipEmptyLines: true,
      delimiter: best?.delimiter || '',
      delimitersToGuess: [',', '\t', ';', '|'],
    }).data || [];
    const headerIndex = matrix.findIndex((line) => {
      const normalized = line.map((cell) => normalizeHeader(String(cell || '')));
      const hasDate = normalized.some((cell) => ['date', 'posting date', 'transaction date', 'booking date'].includes(cell));
      const hasAmount = normalized.some((cell) =>
        ['amount', 'amount usd', 'amount (usd)', 'amount ($)', 'debit', 'credit', 'withdrawal', 'deposit'].includes(cell),
      );
      return hasDate && hasAmount;
    });

    if (headerIndex >= 0 && matrix.length > headerIndex + 1) {
      const headers = matrix[headerIndex].map((cell) => String(cell || '').trim());
      rows = matrix.slice(headerIndex + 1).map((line) => {
        const row: Record<string, string> = {};
        headers.forEach((h, i) => {
          row[h] = String(line[i] ?? '').trim();
        });
        return row;
      });
    }
  }

  if (!rows.length) {
    return { transactions: [], totalRows: 0, skippedInvalid: 0, skippedBalance: 0, warnings: ['CSV sem cabeçalho válido para data/valor.'] };
  }

  const currency: 'BRL' | 'USD' = source === 'itau' ? 'BRL' : 'USD';
  let skippedInvalid = 0;
  let skippedBalance = 0;
  const transactions = rows
    .map((row, idx) => {
      const dateRaw = getColumnValue(row, [
        'date',
        'booking date',
        'posted date',
        'post date',
        'posting date',
        'transaction date',
        'data',
        'data lançamento',
        'data lancamento',
      ]);
      const amountRaw = getColumnValue(row, ['amount', 'amount usd', 'amount (usd)', 'amount ($)', 'valor', 'valor (r$)']);
      const debitRaw = getColumnValue(row, ['debit', 'débito', 'debito', 'withdrawal']);
      const creditRaw = getColumnValue(row, ['credit', 'crédito', 'credito', 'deposit']);
      const typeRaw = getColumnValue(row, ['type', 'tipo', 'transaction type']).toLowerCase();
      const detailsRaw = getColumnValue(row, ['details', 'detail']).toLowerCase();
      const desc = getColumnValue(row, [
        'description',
        'descr',
        'memo',
        'details',
        'histórico',
        'historico',
        'narrative',
        'name',
        'transaction description',
        'descricao',
        'descrição',
      ]);
      const explicitRef = getColumnValue(row, [
        'reference',
        'id',
        'transaction id',
        'documento',
        'doc',
        'document number',
        'auth code',
        'authorization code',
        'fitid',
      ]);

      let signedAmount = parseAmount(amountRaw);
      if (!Number.isFinite(signedAmount) || signedAmount === 0) {
        const debitAmount = parseAmount(debitRaw);
        const creditAmount = parseAmount(creditRaw);
        if (Math.abs(creditAmount) > 0) signedAmount = Math.abs(creditAmount);
        else if (Math.abs(debitAmount) > 0) signedAmount = -Math.abs(debitAmount);
      }
      if (typeRaw.includes('debit') || typeRaw.includes('débito') || typeRaw.includes('debito')) {
        signedAmount = -Math.abs(signedAmount);
      }
      if (typeRaw.includes('credit') || typeRaw.includes('crédito') || typeRaw.includes('credito')) {
        signedAmount = Math.abs(signedAmount);
      }
      if (detailsRaw.includes('debit')) {
        signedAmount = -Math.abs(signedAmount);
      }
      if (detailsRaw.includes('credit')) {
        signedAmount = Math.abs(signedAmount);
      }

      const ref = explicitRef || `${dateRaw}-${signedAmount}-${idx}`;
      const direction: 'in' | 'out' = signedAmount >= 0 ? 'in' : 'out';

      if (!dateRaw || !Number.isFinite(signedAmount) || signedAmount === 0) {
        skippedInvalid += 1;
        return null;
      }
      if (isBalanceSnapshotText(desc)) {
        skippedBalance += 1;
        return null;
      }

      const sourceData = Object.fromEntries(
        Object.entries(row).map(([k, v]) => [k, v == null ? '' : String(v)]),
      );
      const fallbackDesc = desc || Object.values(sourceData).join(' ').trim().slice(0, 140);
      return {
        bank_txn_id: ref || null,
        booked_at: normalizeDate(dateRaw, source),
        amount: Math.abs(signedAmount),
        currency,
        direction,
        description: fallbackDesc || null,
        reference: ref || null,
        dedupe_key: `${source}:${ref}:${Math.abs(signedAmount).toFixed(2)}`,
        raw_payload: sourceData,
      } as ParsedTxn;
    })
    .filter((row): row is ParsedTxn => Boolean(row));

  const warnings: string[] = [];
  if (skippedInvalid > 0) warnings.push(`${skippedInvalid} linha(s) ignorada(s) por data/valor inválidos.`);
  if (skippedBalance > 0) warnings.push(`${skippedBalance} linha(s) de saldo/resumo ignorada(s).`);
  return {
    transactions,
    totalRows: rows.length,
    skippedInvalid,
    skippedBalance,
    warnings,
  };
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const ADJUSTMENT_REASONS = [
  { value: 'reconciliation', label: 'Reconciliação bancária' },
  { value: 'correction', label: 'Correção de lançamento' },
  { value: 'interest', label: 'Juros/Rendimentos' },
  { value: 'fee', label: 'Taxas bancárias' },
  { value: 'exchange', label: 'Ajuste cambial' },
  { value: 'other', label: 'Outro' },
];

export default function BankAccounts() {
  const { t } = useTranslation();
  const [businessUnit, setBusinessUnit] = useState<BusinessUnit>('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  
  // Adjustment modal state
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [adjustingAccount, setAdjustingAccount] = useState<BankAccount | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [viewingHistoryAccount, setViewingHistoryAccount] = useState<BankAccount | null>(null);
  const [importHistoryDialogOpen, setImportHistoryDialogOpen] = useState(false);
  const [viewingImportHistoryAccount, setViewingImportHistoryAccount] = useState<BankAccount | null>(null);
  const [importBatches, setImportBatches] = useState<ImportBatchSummary[]>([]);
  const [loadingImportBatches, setLoadingImportBatches] = useState(false);
  
  const [adjustmentForm, setAdjustmentForm] = useState({
    new_balance: 0,
    adjustment_date: safeFormat(new Date(), 'yyyy-MM-dd'),
    reason: '',
    notes: '',
  });

  const { data: accounts, isLoading, error } = useBankAccounts(businessUnit);
  const createMutation = useCreateBankAccount();
  const updateMutation = useUpdateBankAccount();
  const adjustmentMutation = useCreateBankAccountAdjustment();
  const { data: adjustmentHistory } = useBankAccountAdjustments(viewingHistoryAccount?.id);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    bank_name: '',
    account_number_last4: '',
    business_unit: 'us',
    currency: 'USD',
    initial_balance: 0,
    is_default: false,
  });

  const handleOpenDialog = (account?: BankAccount) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        name: account.name,
        bank_name: account.bank_name || '',
        account_number_last4: account.account_number_last4 || '',
        business_unit: account.business_unit,
        currency: account.currency,
        initial_balance: account.initial_balance || 0,
        is_default: account.is_default || false,
      });
    } else {
      setEditingAccount(null);
      setFormData({
        name: '',
        bank_name: '',
        account_number_last4: '',
        business_unit: 'us',
        currency: 'USD',
        initial_balance: 0,
        is_default: false,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingAccount) {
        await updateMutation.mutateAsync({
          id: editingAccount.id,
          name: formData.name,
          bank_name: formData.bank_name || null,
          account_number_last4: formData.account_number_last4 || null,
          is_default: formData.is_default,
        });
        toast.success(t('finance.accountUpdated', 'Conta atualizada com sucesso'));
      } else {
        await createMutation.mutateAsync({
          name: formData.name,
          bank_name: formData.bank_name || undefined,
          account_number_last4: formData.account_number_last4 || undefined,
          business_unit: formData.business_unit,
          currency: formData.currency,
          initial_balance: formData.initial_balance,
          is_default: formData.is_default,
        });
        toast.success(t('finance.accountCreated', 'Conta criada com sucesso'));
      }
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Error saving bank account:', err);
      toast.error(t('finance.accountError', 'Erro ao salvar conta'));
    }
  };

  const handleOpenAdjustmentDialog = (account: BankAccount) => {
    setAdjustingAccount(account);
    setAdjustmentForm({
      new_balance: account.current_balance || 0,
      adjustment_date: safeFormat(new Date(), 'yyyy-MM-dd'),
      reason: '',
      notes: '',
    });
    setAdjustmentDialogOpen(true);
  };

  const handleAdjustmentSubmit = async () => {
    if (!adjustingAccount || !adjustmentForm.reason) return;

    try {
      await adjustmentMutation.mutateAsync({
        bank_account_id: adjustingAccount.id,
        previous_balance: adjustingAccount.current_balance || 0,
        new_balance: adjustmentForm.new_balance,
        adjustment_date: adjustmentForm.adjustment_date,
        reason: adjustmentForm.reason,
        notes: adjustmentForm.notes || undefined,
      });
      toast.success('Saldo ajustado com sucesso!');
      setAdjustmentDialogOpen(false);
    } catch (err) {
      console.error('Error adjusting balance:', err);
      toast.error('Erro ao ajustar saldo');
    }
  };

  const handleViewHistory = (account: BankAccount) => {
    setViewingHistoryAccount(account);
    setHistoryDialogOpen(true);
  };

  const handleViewImportHistory = async (account: BankAccount) => {
    setViewingImportHistoryAccount(account);
    setImportHistoryDialogOpen(true);
    setLoadingImportBatches(true);
    try {
      const source = inferImportSourceFromAccount(account);
      const { data, error } = await (supabase.from('bank_import_batches' as any) as any)
        .select(
          'id,source,file_name,file_format,parse_status,parse_error,imported_at,total_rows_received,total_rows_parsed,inserted_normalized,skipped_duplicates,skipped_invalid,warnings',
        )
        .eq('source', source)
        .order('imported_at', { ascending: false })
        .limit(30);
      if (error) throw error;
      setImportBatches((data || []) as ImportBatchSummary[]);
    } catch (err: any) {
      console.error('Error loading import batches:', err);
      toast.error(`Falha ao carregar imports: ${err.message || 'erro desconhecido'}`);
      setImportBatches([]);
    } finally {
      setLoadingImportBatches(false);
    }
  };

  const formatCurrency = (value: number, currency: string) => {
    return formatMoney(value, (currency as 'USD' | 'BRL') || 'USD');
  };

  const handleBankImportFile = async (file: File, source: ImportSource, account?: BankAccount) => {
    try {
      setIsImporting(true);
      const content = await file.text();
      const lower = file.name.toLowerCase();
      const isOfxLike = lower.endsWith('.ofx') || lower.endsWith('.ofc');
      const parseResult = isOfxLike ? parseOfx(content, source) : parseCsv(content, source);
      const parsed = parseResult.transactions;
      const ledgerBalance = isOfxLike && source === 'itau' ? extractOfxLedgerBalance(content) : null;

      if (!parsed.length) {
        toast.error('Nenhuma transação encontrada no arquivo');
        return;
      }

      const hash = await sha256Hex(content);
      const fileFormat = isOfxLike ? 'ofx' : lower.endsWith('.txt') ? 'txt' : 'csv';

      const { data, error } = await supabase.functions.invoke('import-bank-transactions', {
        body: {
          source,
          file_name: file.name,
          file_hash: hash,
          file_format: fileFormat,
          transactions: parsed,
          total_rows_received: parseResult.totalRows,
          total_rows_parsed: parsed.length + parseResult.skippedInvalid + parseResult.skippedBalance,
          skipped_invalid: parseResult.skippedInvalid + parseResult.skippedBalance,
          warnings: parseResult.warnings,
        },
      });
      if (error) throw error;
      const inserted = Number(data?.inserted_normalized ?? 0);
      const skipped = Number(data?.skipped_duplicates ?? 0);
      if (data?.duplicate_file) {
        toast.info(`Arquivo já importado. Duplicadas ignoradas: ${skipped}.`);
      } else {
        toast.success(`Importação concluída: ${inserted} nova(s), ${skipped} duplicada(s) ignorada(s).`);
      }
      if (parseResult.warnings.length > 0) {
        toast.info(parseResult.warnings.join(' '));
      }

      if (account && ledgerBalance !== null) {
        await updateMutation.mutateAsync({
          id: account.id,
          current_balance: ledgerBalance,
        });
        toast.success(`Saldo da conta atualizado pelo extrato: ${formatCurrency(ledgerBalance, account.currency)}`);
      }

    } catch (err: any) {
      console.error('Bank import error:', err);
      toast.error(`Falha ao importar: ${err.message || 'erro desconhecido'}`);
    } finally {
      setIsImporting(false);
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center text-red-500 py-8">
          {t('finance.errorLoadingAccounts', 'Erro ao carregar contas bancárias')}
        </div>
      </div>
    );
  }

  return (
    <FinancePageLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-foreground">
          {t('finance.bankAccounts', 'Contas Bancárias')}
        </h1>
        
        <div className="flex gap-2">
          <BusinessUnitSelector 
            value={businessUnit} 
            onChange={setBusinessUnit}
            className="w-32"
          />
          <Button onClick={() => handleOpenDialog()} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('finance.newAccount', 'Nova Conta')}
          </Button>
        </div>
      </div>

      {/* Accounts Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : accounts?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {t('finance.noAccounts', 'Nenhuma conta bancária')}
            </h3>
            <p className="text-muted-foreground mb-4">
              {t('finance.noAccountsDescription', 'Crie sua primeira conta bancária para começar')}
            </p>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              {t('finance.createAccount', 'Criar Conta')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts?.map((account) => (
            <Card key={account.id} className="relative">
              {account.is_default && (
                <div className="absolute top-2 right-2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                  {t('finance.default', 'Padrão')}
                </div>
              )}
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{account.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {account.bank_name || t('finance.noBank', 'Banco não informado')}
                      {account.account_number_last4 && ` ••••${account.account_number_last4}`}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('finance.businessUnit', 'Unidade')}</span>
                  <span className="font-medium">
                    {account.business_unit === 'us' ? '🇺🇸 US' : '🇧🇷 BR'} • {account.currency}
                  </span>
                </div>

                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground mb-1">{t('finance.currentBalance', 'Saldo Atual')}</p>
                  <p className="text-2xl font-bold">
                    <MoneyDisplay 
                      value={account.current_balance} 
                      currency={account.currency as 'USD' | 'BRL'} 
                    />
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 gap-2"
                    onClick={() => handleOpenDialog(account)}
                  >
                    <Pencil className="h-4 w-4" />
                    {t('finance.edit', 'Editar')}
                  </Button>
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="flex-1 gap-2"
                    onClick={() => handleOpenAdjustmentDialog(account)}
                  >
                    <Settings2 className="h-4 w-4" />
                    Ajustar
                  </Button>
                </div>
                <div className="space-y-2">
                  <Input
                    id={`import-file-${account.id}`}
                    type="file"
                    className="hidden"
                    accept={inferImportSourceFromAccount(account) === 'itau' ? '.ofx,.ofc,.csv,.txt,text/ofx,text/plain,text/csv' : '.csv,.txt,text/plain,text/csv'}
                    disabled={isImporting}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      await handleBankImportFile(file, inferImportSourceFromAccount(account), account);
                      e.currentTarget.value = '';
                    }}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full gap-2"
                    disabled={isImporting}
                    onClick={() => {
                      const input = document.getElementById(`import-file-${account.id}`) as HTMLInputElement | null;
                      input?.click();
                    }}
                  >
                    {isImporting ? <History className="h-4 w-4 animate-pulse" /> : <ArrowUpRight className="h-4 w-4" />}
                    Upload Extrato ({inferImportSourceFromAccount(account).toUpperCase()})
                  </Button>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full gap-2 text-muted-foreground"
                  asChild
                >
                  <Link to={`/finance/bank-accounts/${account.id}/transactions`}>
                    <History className="h-4 w-4" />
                    Ver Transações da Conta
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full gap-2 text-muted-foreground"
                  onClick={() => handleViewImportHistory(account)}
                >
                  <History className="h-4 w-4" />
                  Ver Histórico de Imports
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full gap-2 text-muted-foreground"
                  onClick={() => handleViewHistory(account)}
                >
                  <History className="h-4 w-4" />
                  Ver Histórico de Ajustes
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAccount 
                ? t('finance.editAccount', 'Editar Conta Bancária')
                : t('finance.newAccount', 'Nova Conta Bancária')
              }
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('finance.accountName', 'Nome da Conta')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Chase Orlando"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank_name">{t('finance.bankName', 'Nome do Banco')}</Label>
              <Input
                id="bank_name"
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                placeholder="Ex: Chase Bank"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_number">{t('finance.lastDigits', 'Últimos 4 dígitos')}</Label>
              <Input
                id="account_number"
                value={formData.account_number_last4}
                onChange={(e) => setFormData({ ...formData, account_number_last4: e.target.value.slice(0, 4) })}
                placeholder="1234"
                maxLength={4}
              />
            </div>

            {!editingAccount && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('finance.businessUnit', 'Unidade de Negócio')} *</Label>
                    <Select
                      value={formData.business_unit}
                      onValueChange={(v) => setFormData({ 
                        ...formData, 
                        business_unit: v,
                        currency: v === 'br' ? 'BRL' : 'USD',
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="us">🇺🇸 US</SelectItem>
                        <SelectItem value="br">🇧🇷 BR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('finance.currency', 'Moeda')} *</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(v) => setFormData({ ...formData, currency: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="BRL">BRL (R$)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="initial_balance">{t('finance.initialBalance', 'Saldo Inicial')}</Label>
                  <Input
                    id="initial_balance"
                    type="number"
                    step="0.01"
                    value={formData.initial_balance}
                    onChange={(e) => setFormData({ ...formData, initial_balance: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </>
            )}

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>{t('finance.defaultAccount', 'Conta Padrão')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('finance.defaultAccountDesc', 'Usar como conta principal')}
                </p>
              </div>
              <Switch
                checked={formData.is_default}
                onCheckedChange={(v) => setFormData({ ...formData, is_default: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
            >
              {editingAccount ? t('common.save', 'Salvar') : t('common.create', 'Criar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Adjustment Dialog */}
      <Dialog open={adjustmentDialogOpen} onOpenChange={setAdjustmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Ajustar Saldo - {adjustingAccount?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Saldo Atual</Label>
              <div className="p-3 bg-muted rounded-lg font-mono text-lg">
                {adjustingAccount && formatCurrency(adjustingAccount.current_balance || 0, adjustingAccount.currency)}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_balance">Novo Saldo *</Label>
              <Input
                id="new_balance"
                type="number"
                step="0.01"
                value={adjustmentForm.new_balance}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, new_balance: parseFloat(e.target.value) || 0 })}
              />
              {adjustingAccount && (
                <p className={`text-sm ${adjustmentForm.new_balance - (adjustingAccount.current_balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  Diferença: {formatCurrency(adjustmentForm.new_balance - (adjustingAccount.current_balance || 0), adjustingAccount.currency)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="adjustment_date">Data do Ajuste *</Label>
              <Input
                id="adjustment_date"
                type="date"
                value={adjustmentForm.adjustment_date}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, adjustment_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Select
                value={adjustmentForm.reason}
                onValueChange={(v) => setAdjustmentForm({ ...adjustmentForm, reason: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o motivo" />
                </SelectTrigger>
                <SelectContent>
                  {ADJUSTMENT_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Observações</Label>
              <Textarea
                id="notes"
                placeholder="Detalhes adicionais sobre o ajuste..."
                value={adjustmentForm.notes}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustmentDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAdjustmentSubmit}
              disabled={!adjustmentForm.reason || adjustmentMutation.isPending}
            >
              {adjustmentMutation.isPending ? 'Salvando...' : 'Ajustar Saldo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Histórico de Ajustes - {viewingHistoryAccount?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="max-h-[400px] overflow-y-auto">
            {adjustmentHistory?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Nenhum ajuste registrado para esta conta.
              </p>
            ) : (
              <div className="space-y-3">
                {adjustmentHistory?.map((adj: any) => (
                  <div key={adj.id} className="p-3 border rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {ADJUSTMENT_REASONS.find(r => r.value === adj.reason)?.label || adj.reason}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {safeFormat(new Date(adj.adjustment_date), 'dd/MM/yyyy')}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        {viewingHistoryAccount && formatCurrency(adj.previous_balance, viewingHistoryAccount.currency)}
                      </span>
                      <span className="text-muted-foreground">→</span>
                      <span className="font-medium">
                        {viewingHistoryAccount && formatCurrency(adj.new_balance, viewingHistoryAccount.currency)}
                      </span>
                      <span className={`font-medium ${adj.adjustment_amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        ({adj.adjustment_amount >= 0 ? '+' : ''}{viewingHistoryAccount && formatCurrency(adj.adjustment_amount, viewingHistoryAccount.currency)})
                      </span>
                    </div>
                    {adj.notes && (
                      <p className="text-sm text-muted-foreground">{adj.notes}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Por: {adj.created_by_user?.display_name || adj.created_by_user?.first_name || 'Sistema'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import History Dialog */}
      <Dialog open={importHistoryDialogOpen} onOpenChange={setImportHistoryDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Histórico de Imports - {viewingImportHistoryAccount?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="max-h-[500px] overflow-y-auto space-y-3">
            {loadingImportBatches ? (
              <p className="text-sm text-muted-foreground">Carregando imports...</p>
            ) : importBatches.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Nenhum lote encontrado para esta conta.</p>
            ) : (
              importBatches.map((batch) => (
                <div key={batch.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{batch.file_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(batch.imported_at).toLocaleString()} • {batch.source.toUpperCase()} • {batch.file_format.toUpperCase()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Status</p>
                      <p className={`text-xs font-semibold ${batch.parse_status === 'failed' ? 'text-red-600' : 'text-emerald-600'}`}>
                        {batch.parse_status}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                    <div className="border rounded p-2">
                      <p className="text-muted-foreground">Lidas</p>
                      <p className="font-semibold">{batch.total_rows_received || 0}</p>
                    </div>
                    <div className="border rounded p-2">
                      <p className="text-muted-foreground">Parseadas</p>
                      <p className="font-semibold">{batch.total_rows_parsed || 0}</p>
                    </div>
                    <div className="border rounded p-2">
                      <p className="text-muted-foreground">Novas</p>
                      <p className="font-semibold text-emerald-600">{batch.inserted_normalized || 0}</p>
                    </div>
                    <div className="border rounded p-2">
                      <p className="text-muted-foreground">Duplicadas</p>
                      <p className="font-semibold text-amber-600">{batch.skipped_duplicates || 0}</p>
                    </div>
                    <div className="border rounded p-2">
                      <p className="text-muted-foreground">Inválidas</p>
                      <p className="font-semibold text-red-600">{batch.skipped_invalid || 0}</p>
                    </div>
                  </div>
                  {batch.warnings && batch.warnings.length > 0 ? (
                    <p className="text-xs text-muted-foreground">{batch.warnings.join(' ')}</p>
                  ) : null}
                  {batch.parse_error ? (
                    <p className="text-xs text-red-600">Erro: {batch.parse_error}</p>
                  ) : null}
                  <div className="pt-1">
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/finance/bank-accounts/${viewingImportHistoryAccount?.id}/transactions?batch_id=${batch.id}`}>
                        Ver transações deste lote
                      </Link>
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportHistoryDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FinancePageLayout>
  );
}
